import { useEffect, useState } from 'react'
import { HashRouter, BrowserRouter, useLocation, useNavigate } from 'react-router-dom'
import { useDeepLink } from './hooks/useDeepLink'
import { useModeInitialization } from './hooks/useModeInitialization'
import { useDevReset } from './hooks/useDevReset'
import { AuthProvider, useAuth } from './contexts/AuthProvider'
import { getPlatformAPI } from './lib/platform'
import { trackPageview } from './lib/analytics'
import { dispatchAppEvent } from './lib/events'
import { migrateOfflineDataToDexie } from './lib/db/migrations'
import { getSetupFlowProgress } from './lib/supabase/setupFlow'
import { getRouteForStep } from './lib/types/setupFlow'
import { navigateToHome } from './routes/navigateToHome'
import { Toaster } from 'react-hot-toast'
import { WebRoutes } from './routes/WebRoutes'
import { ElectronRoutes } from './routes/ElectronRoutes'
import { UpdateDialog } from './components/layout/UpdateDialog'
import { UpdateStatusProvider } from './contexts/UpdateStatusProvider'

// Apply theme immediately - before component even renders
const applyInitialTheme = () => {
  if (document.body.classList.contains('theme-initialized')) return

  // HARDCODED: Always use light theme for Setup/Onboarding pages
  document.body.classList.remove('dark')
  document.body.classList.add('light')
  document.body.classList.add('theme-initialized')
}

// Run immediately when module loads
applyInitialTheme()

// Route Tracker Component - Saves current route and updates window properties
function RouteTracker() {
  const { pathname } = useLocation()
  const platformAPI = getPlatformAPI()

  useEffect(() => {
    dispatchAppEvent('route-change', pathname)

    if (platformAPI.platform === 'electron' && platformAPI.api?.invoke) {
      platformAPI.api.invoke('save-current-route', pathname)
    }
    trackPageview(pathname)
  }, [pathname, platformAPI])

  return null
}

// Setup Flow Auto-Resume Component - CENTRAL ROUTING LOGIC
// This is the ONLY component responsible for setup flow navigation
function SetupFlowAutoResume() {
  const { isAuthenticated, loading, user } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [hasChecked, setHasChecked] = useState(false)

  useEffect(() => {
    const autoResumeSetupFlow = async () => {
      if (loading) return
      if (!isAuthenticated || !user) return
      if (hasChecked) return

      // Während eines Password-Recovery-Flows NICHT weg-navigieren — SetupPage
      // muss die Chance haben, ResetPasswordForm zu rendern. Supabase etabliert
      // beim Klick auf den Recovery-Link bereits eine Session, deshalb würden
      // wir ohne diesen Guard direkt zu /home oder /setup-profile springen.
      if (localStorage.getItem('passwordRecoveryMode') === 'true') {
        setHasChecked(true)
        return
      }

      const loginMethod = localStorage.getItem('loginMethod')
      if (loginMethod === 'offline') {
        setHasChecked(true)
        return
      }

      const isHomeRoute = pathname === '/home' ||
                          pathname.startsWith('/home/') ||
                          window.location.hash === '#/home' ||
                          window.location.hash.startsWith('#/home');
      if (isHomeRoute) {
        setHasChecked(true)
        return
      }

      const isOAuthCallback = pathname === '/auth/callback' ||
                              window.location.hash === '#/auth/callback' ||
                              window.location.hash.startsWith('#/auth/callback');
      if (isOAuthCallback) {
        setHasChecked(true)
        return
      }

      const isOnboardingRoute = pathname === '/onboarding' ||
                                window.location.hash === '#/onboarding' ||
                                window.location.hash.startsWith('#/onboarding');
      if (isOnboardingRoute) {
        setHasChecked(true)
        return
      }

      try {
        const progress = await getSetupFlowProgress()

        if (!progress) {
          navigate('/setup-profile', { replace: true })
          setHasChecked(true)
          return
        }

        if (progress.completed) {
          // Server is source of truth — rehydrate localStorage (wiped on logout)
          // so ProtectedRoute and other localStorage-based checks stay in sync.
          localStorage.setItem('onboarding_completed', 'true')
          setHasChecked(true)
          await navigateToHome(navigate)
          return
        }

        const targetRoute = getRouteForStep(progress.step)

        if (pathname === targetRoute) {
          setHasChecked(true)
          return
        }

        navigate(targetRoute, { replace: true })
        setHasChecked(true)
      } catch (error) {
        console.error('[SetupFlowAutoResume] Error:', error)
        setHasChecked(true)
      }
    }

    autoResumeSetupFlow()
  }, [isAuthenticated, loading, user, pathname, hasChecked, navigate])

  return null
}

// Deep Link Handler Wrapper Component
function DeepLinkHandler() {
  useDeepLink()
  return null
}

function App() {
  useModeInitialization()
  useDevReset()

  const platformAPI = getPlatformAPI()

  // Run migration on app start
  useEffect(() => {
    migrateOfflineDataToDexie()
  }, [])

  // Windows platform class for CSS corrections (DirectWrite vs CoreText font metrics)
  useEffect(() => {
    if (platformAPI.platform === 'electron' && navigator.userAgent.includes('Windows')) {
      document.documentElement.classList.add('platform-win')
    }
  }, [])

  const Router = platformAPI.platform === 'web' ? BrowserRouter : HashRouter

  return (
    <Router>
      <AuthProvider>
        <UpdateStatusProvider>
          <UpdateDialog />
          <DeepLinkHandler />
          <RouteTracker />
          <SetupFlowAutoResume />
          {platformAPI.platform === 'web' ? <WebRoutes /> : <ElectronRoutes />}
          <Toaster position="bottom-center" toastOptions={{ duration: 5000 }} />
        </UpdateStatusProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
